import { useEffect, useState, type FormEvent } from "react";
import { Navigate } from "react-router-dom";
import {
  consumeMagicLinkCode,
  fetchProviders,
  requestMagicLink,
  toSession,
  type MagicLinkDelivery,
  type Providers,
} from "../auth/api";
import { useAuth } from "../auth/context";

type SubmitState =
  | { kind: "idle" }
  | { kind: "sending" }
  | { kind: "sent"; email: string; delivery: MagicLinkDelivery }
  | { kind: "error"; message: string };

export function SignIn() {
  const { state, setSession } = useAuth();
  const [email, setEmail] = useState("");
  const [remember, setRemember] = useState(true);
  const [submit, setSubmit] = useState<SubmitState>({ kind: "idle" });
  const [providers, setProviders] = useState<Providers | null>(null);
  const [code, setCode] = useState("");
  const [codeBusy, setCodeBusy] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchProviders()
      .then((p) => {
        if (!cancelled) setProviders(p);
      })
      .catch(() => {
        // Best-effort: if discovery fails, fall back to showing only
        // magic-link (the universally-available method).
        if (!cancelled)
          setProviders({ magic_link: true, google: false, microsoft: false });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.status === "authenticated") return <Navigate to="/" replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!email) return;
    setSubmit({ kind: "sending" });
    try {
      const { delivery } = await requestMagicLink(email, remember);
      setSubmit({ kind: "sent", email, delivery });
    } catch (err) {
      setSubmit({
        kind: "error",
        message: err instanceof Error ? err.message : String(err),
      });
    }
  }

  const showGoogle = providers?.google ?? false;
  const showMicrosoft = providers?.microsoft ?? false;
  const showOAuthRow = showGoogle || showMicrosoft;

  return (
    <main className="mx-auto flex min-h-full max-w-md flex-col justify-center gap-6 p-8">
      <header className="space-y-1">
        <img
          src="/seneca-logo.svg"
          alt="Seneca"
          className="h-11 w-auto self-start dark:hidden"
        />
        <img
          src="/seneca-logo-dark.svg"
          alt="Seneca"
          className="hidden h-11 w-auto self-start dark:block"
        />
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Sign in with a one-time email link.
        </p>
      </header>

      <form
        onSubmit={onSubmit}
        className="space-y-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-900"
      >
        <label className="block space-y-1">
          <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Email</span>
          <input
            type="email"
            required
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500 dark:border-slate-600 dark:bg-slate-800"
            placeholder="you@example.com"
            autoComplete="email"
          />
        </label>

        <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
          <input
            type="checkbox"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 dark:border-slate-600"
          />
          Remember this device for 30 days
        </label>

        <button
          type="submit"
          disabled={submit.kind === "sending" || !email}
          className="w-full rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
        >
          {submit.kind === "sending" ? "Sending…" : "Send sign-in link"}
        </button>

        {submit.kind === "sent" && (
          <Result email={submit.email} delivery={submit.delivery} />
        )}
        {submit.kind === "error" && (
          <p className="text-sm text-rose-700 dark:text-rose-300">
            Couldn't send: <code className="font-mono">{submit.message}</code>
          </p>
        )}
      </form>

      {submit.kind === "sent" && (
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            const digits = code.replace(/\D/g, "");
            if (digits.length !== 6) {
              setCodeError("Code must be 6 digits.");
              return;
            }
            setCodeBusy(true);
            setCodeError(null);
            try {
              const bundle = await consumeMagicLinkCode(submit.email, digits);
              await setSession(toSession(bundle));
            } catch (err) {
              setCodeError(
                err instanceof Error ? err.message : String(err),
              );
            } finally {
              setCodeBusy(false);
            }
          }}
          className="space-y-3 rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-900"
        >
          <div className="space-y-1">
            <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
              On iOS or can't open the link in this app?
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Type the 6-digit code from the same email here instead.
            </p>
          </div>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="one-time-code"
            maxLength={7}
            placeholder="123 456"
            className="block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-center text-xl font-mono tracking-[0.4em] shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500 dark:border-slate-600 dark:bg-slate-800"
          />
          <button
            type="submit"
            disabled={codeBusy || code.replace(/\D/g, "").length !== 6}
            className="w-full rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
          >
            {codeBusy ? "Signing in…" : "Sign in with code"}
          </button>
          {codeError && (
            <p className="text-sm text-rose-700 dark:text-rose-300">
              Couldn't sign in: <code className="font-mono">{codeError}</code>
            </p>
          )}
        </form>
      )}

      {showOAuthRow && (
        <p className="text-center text-xs text-slate-500 dark:text-slate-400">
          Or sign in with{" "}
          {showGoogle && (
            <a className="underline hover:text-slate-700 dark:hover:text-slate-200" href="/api/auth/google">
              Google
            </a>
          )}
          {showGoogle && showMicrosoft && " · "}
          {showMicrosoft && (
            <a
              className="underline hover:text-slate-700 dark:hover:text-slate-200"
              href="/api/auth/microsoft"
            >
              Microsoft
            </a>
          )}
        </p>
      )}
    </main>
  );
}

function Result({
  email,
  delivery,
}: {
  email: string;
  delivery: MagicLinkDelivery;
}) {
  if (delivery.sent) {
    return (
      <p className="text-sm text-emerald-700 dark:text-emerald-300">
        Sent. Check <strong>{email}</strong> for a sign-in link. The link
        expires in 15 minutes.
      </p>
    );
  }
  return (
    <p className="text-sm text-amber-700 dark:text-amber-300">
      Magic-link generated, but email isn't configured yet (
      <code className="font-mono">{delivery.reason}</code>). The link was
      logged to the API container's stdout — check with{" "}
      <code className="font-mono">docker compose logs api</code>.
    </p>
  );
}
